import { useState, useEffect } from "react";
import { X, RefreshCw, Bell, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  requestNotificationPermission,
  isOneSignalAvailable,
  getPermissionStatus,
  OneSignalError,
} from "@lyder-talk/core";

interface OneSignalRetryBannerProps {
  /** Error message to display */
  errorMessage?: string;
  /** Callback when retry is clicked */
  onRetry: () => void;
  /** Callback when banner is dismissed */
  onDismiss?: () => void;
  /** Whether retry is in progress */
  isRetrying?: boolean;
}

export const OneSignalRetryBanner = ({
  errorMessage,
  onRetry,
  onDismiss,
  isRetrying = false,
}: OneSignalRetryBannerProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | null>(null);
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);

  // Check notification permission status (same logic as ProfileModal)
  useEffect(() => {
    const checkNotificationPermission = () => {
      try {
        const status = getPermissionStatus();
        if (status.browser === "granted") {
          setNotificationPermission("granted");
        } else if (status.browser === "denied") {
          setNotificationPermission("denied");
        } else {
          setNotificationPermission("default");
        }
      } catch (error) {
        console.error("Error checking notification permission:", error);
        if ("Notification" in window) {
          setNotificationPermission(Notification.permission);
        }
      }
    };

    checkNotificationPermission();

    const interval = setInterval(() => {
      if (isOneSignalAvailable() && window.OneSignal?.User?.PushSubscription) {
        checkNotificationPermission();
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Sync internal visibility with props
  useEffect(() => {
    if (errorMessage) {
      setIsVisible(true);
    }
  }, [errorMessage]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const handleRequestNotificationPermission = async () => {
    setIsRequestingPermission(true);
    try {
      const result = await requestNotificationPermission();
      if (result.success) {
        toast({
          title: "Notifications Enabled",
          description: "You will now receive push notifications for session shares.",
        });
        const status = getPermissionStatus();
        setNotificationPermission(status.browser === "granted" && status.oneSignal ? "granted" : "default");
        // Hide banner on success
        setIsVisible(false);
        onDismiss?.();
      } else {
        const error = result.error;
        let title = "Permission Denied";
        let description = "Please enable notifications in your browser settings to receive push notifications.";

        if (error) {
          switch (error.code) {
            case "BROWSER_PERMISSION_DENIED":
              title = "Permission Denied";
              description = "Browser notification permission was denied. Please enable it in your browser settings.";
              break;
            case "PERMISSION_DENIED":
              title = "Permission Denied";
              description = "Notification permission was denied. Please enable it in your browser settings.";
              break;
            case "PERMISSION_NOT_GRANTED":
              title = "Permission Not Granted";
              description = "Notification permission was not granted. Please try again.";
              break;
            case "NOT_AUTHENTICATED":
              title = "Sign In Required";
              description = "You must be signed in to enable notifications.";
              break;
            case "NOT_AVAILABLE":
            case "SLIDEDOWN_NOT_AVAILABLE":
            case "SUBSCRIPTION_NOT_AVAILABLE":
              title = "Service Unavailable";
              description = "Notification service is not available. Please refresh the page and try again.";
              break;
            default:
              title = "Error";
              description = error.message || "Failed to request notification permission. Please try again.";
          }
        }

        toast({
          title,
          description,
          variant: "destructive",
        });

        const status = getPermissionStatus();
        if (status.browser === "denied") {
          setNotificationPermission("denied");
        } else if (window.OneSignal?.User?.PushSubscription) {
          const isSubscribed = window.OneSignal.User.PushSubscription.optedIn;
          setNotificationPermission(isSubscribed ? "granted" : "default");
        } else if ("Notification" in window) {
          setNotificationPermission(Notification.permission);
        }
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      const errorMessage = error instanceof OneSignalError
        ? error.message
        : "Failed to request notification permission. Please try again.";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRequestingPermission(false);
    }
  };

  // Determine banner visibility: show if there's an error OR if notifications are not granted
  const shouldShowBanner = isVisible && notificationPermission !== null && (errorMessage || notificationPermission !== "granted");

  // Don't render if notifications are granted and no error
  if (!shouldShowBanner) {
    return null;
  }

  // Determine the banner style based on state
  const isDenied = notificationPermission === "denied";
  const isError = !!errorMessage;
  const isNotEnabled = notificationPermission === "default" && !errorMessage;

  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${shouldShowBanner ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      aria-hidden={!shouldShowBanner}
    >
      <div className="overflow-hidden">
        <div className={cn(
          "relative z-50 border-b backdrop-blur-md shadow-lg",
          isError || isDenied
            ? "border-destructive/20 bg-gradient-to-r from-destructive/15 via-background to-destructive/15"
            : "border-yellow-500/20 bg-gradient-to-r from-yellow-500/15 via-background to-yellow-500/15"
        )}>
          {/* Animated background glow */}
          <div className={cn(
            "absolute inset-0 animate-pulse",
            isError || isDenied
              ? "bg-gradient-to-r from-destructive/5 via-transparent to-destructive/5"
              : "bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5"
          )} />

          <div className="container mx-auto px-2 relative">
              <div className="flex items-center justify-center gap-2 sm:gap-4 py-1.5 sm:py-2">
              <div className="flex items-center gap-2 sm:gap-4">
                {/* Icon */}
                <div className="relative flex-shrink-0">
                  <div className={cn(
                    "relative w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shadow-md",
                    isError || isDenied
                      ? "bg-gradient-to-br from-destructive/20 to-destructive/40"
                      : "bg-gradient-to-br from-yellow-500/20 to-yellow-600/40"
                  )}>
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                </div>

                {/* Text Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {isError
                        ? t("pages.push.unavailable", "Push Notifications Unavailable")
                        : isDenied
                          ? t("pages.push.blocked", "Push Notifications Blocked")
                          : t("pages.push.enable", "Enable Push Notifications")
                      }
                    </p>
                    {(errorMessage || isDenied || isNotEnabled) && (
                      <p className="text-xs text-muted-foreground truncate hidden sm:block">
                        {errorMessage
                          ? errorMessage
                          : isDenied
                            ? t("pages.push.unblockHint", "Please unblock in browser settings")
                            : t("pages.push.notified", "Get notified when sessions are shared")
                        }
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {isError ? (
                  <Button
                    size="sm"
                    onClick={onRetry}
                    disabled={isRetrying}
                    className="bg-destructive/15 hover:bg-destructive/25 text-destructive font-medium h-7 sm:h-8 px-2 sm:px-3"
                  >
                    <RefreshCw
                      className={cn(
                        "h-3 w-3 mr-1 hidden sm:block",
                        isRetrying && "animate-spin"
                      )}
                    />
                    {isRetrying ? t("pages.push.retrying", "Retrying...") : t("pages.push.retry", "Retry")}
                  </Button>
                ) : isDenied ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onRetry}
                    disabled={isRetrying}
                    className="font-medium h-7 sm:h-8 px-2 sm:px-3"
                  >
                    <RefreshCw className={cn("h-3 w-3 mr-1 hidden sm:block", isRetrying && "animate-spin")} />
                    {t("pages.push.refresh", "Refresh")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleRequestNotificationPermission}
                    disabled={isRequestingPermission}
                    className="bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-600 font-medium h-7 sm:h-8 px-2 sm:px-3"
                  >
                    {isRequestingPermission ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin mr-1 hidden sm:block" />
                        {t("pages.push.enabling", "Enabling...")}
                      </>
                    ) : (
                      <>
                        {t("pages.push.enableButton", "Enable")}
                      </>
                    )}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-all duration-200"
                  onClick={handleDismiss}
                  aria-label={t("pages.notifications.dismiss", "Dismiss notification")}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
